import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient, setToken } from '../lib/api';

interface User {
  id: number;
  username: string;
  email: string | null;
  role: 'admin' | 'user';
  // Two-tier admin: only super-admin sees Postback Forwarding/Logs/Audit Log
  // menus. Always false when role !== 'admin'.
  isSuperAdmin: boolean;
  publisherId: number | null;
  displayName: string | null;
  avatarUrl: string | null;
  themePreference: string;
  timezone: string;
  mustChangePassword: boolean;
  lastLoginAt: number | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  changePassword: (current: string, next: string) => Promise<void>;
  fetchMe: () => Promise<void>;
  setTheme: (theme: string) => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      loading: false,
      error: null,

      login: async (username, password) => {
        set({ loading: true, error: null });
        try {
          const data = await apiClient.post<{ token: string; user: User }>('/api/v1/auth/login', {
            username,
            password,
          });
          setToken(data.token);
          set({ user: data.user, token: data.token, loading: false });
          // Apply theme from user pref
          applyTheme(data.user.themePreference);
        } catch (e) {
          set({ loading: false, error: (e as Error).message });
          throw e;
        }
      },

      logout: () => {
        apiClient.post('/api/v1/auth/logout').catch(() => {});
        setToken(null);
        // Also clear the Zustand persist snapshot so rehydration on next page
        // load doesn't restore stale user/token state.
        localStorage.removeItem('rj_auth');
        set({ user: null, token: null });
        // Remove dark/theme classes so the next visitor starts from a clean slate
        // (D15: applyTheme on logout — prevents stale dark class after sign-out)
        applyTheme('cyberpunk-dark');
      },

      changePassword: async (current, next) => {
        await apiClient.post('/api/v1/auth/change-password', {
          currentPassword: current,
          newPassword: next,
        });
        // Refresh user data
        await get().fetchMe();
      },

      fetchMe: async () => {
        try {
          const user = await apiClient.get<User>('/api/v1/auth/me');
          set({ user });
          applyTheme(user.themePreference);
        } catch (e: any) {
          // Only invalidate auth on 401 (expired/invalid token). Transient errors
          // (5xx, network blip) should leave the token alone so the next request
          // can retry rather than booting the user to login on every flake.
          if (e?.status === 401) {
            set({ user: null, token: null });
            setToken(null);
          } else {
            console.warn('fetchMe failed (non-401, keeping session)', e);
          }
        }
      },

      setTheme: (theme) => {
        applyTheme(theme);
        const u = get().user;
        if (u) set({ user: { ...u, themePreference: theme } });
        apiClient.patch('/api/v1/auth/me', { themePreference: theme }).catch(() => {});
      },
    }),
    {
      name: 'rj_auth',
      partialize: (s) => ({ token: s.token, user: s.user }),
    },
  ),
);

export function applyTheme(theme: string) {
  const html = document.documentElement;
  html.classList.remove(
    'theme-cyberpunk-dark',
    'theme-cyberpunk-neon',
    'theme-cyberpunk-mono',
    'theme-light',
    'dark',
  );
  html.classList.add(`theme-${theme}`);
  if (theme !== 'light') html.classList.add('dark');
}
